import { useCurrentStateAndParams } from '@uirouter/react';

import { useCurrentUser } from '@/react/hooks/useUser';
import { isPureAdmin } from '@/portainer/users/user.helpers';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { useRegistry } from '../../queries/useRegistry';
import { useRegistryRepositories } from '../queries/useRegistryRepositories';
import { useRegistryPing } from '../../queries/useRegistryPing';
import { Registry } from '../../types/registry';
import { useEnvironmentType } from '../../../environments/queries/useEnvironmentType';

import { Repository } from './types';

function getRegistriesLink(environmentType: string) {
  switch (environmentType) {
    case 'docker':
      return 'docker.host.registries';
    case 'swarm':
      return 'docker.swarm.registries';
    case 'kubernetes':
      return 'kubernetes.registries';
    default:
      return 'portainer.registries';
  }
}

function getBreadcrumbsForRegistry(
  registry: Registry | undefined,
  isAdminUser: boolean
) {
  if (!registry) {
    return [];
  }

  if (isAdminUser) {
    return [
      {
        label: registry.Name,
        link: 'portainer.registries.registry',
        linkParams: { id: registry.Id },
      },
    ];
  }

  return [registry.Name];
}

export function useRepositoryViewData() {
  const { params } = useCurrentStateAndParams();
  const { user } = useCurrentUser();
  const isAdminUser = isPureAdmin(user);

  const registryId = parseInt(params.id, 10);
  const registryQuery = useRegistry(registryId);

  // Do a ping to make sure things are configured correctly
  const pingQuery = useRegistryPing(registryId, {
    enabled: !!registryQuery.data,
  });
  const registry = registryQuery.data;

  // Get the catalog of repositories
  const repositoriesQuery = useRegistryRepositories(registryId, registry, {
    enabled: !!registry && pingQuery.isSuccess,
  });

  const showInvalidConfig =
    (pingQuery.isError || repositoriesQuery.isError) && !!registry;

  const repositories: Repository[] = repositoriesQuery.data || [];

  // Get the info required to build the breadcrumbs
  const environmentId = useEnvironmentId(false);
  const environmentType = useEnvironmentType(environmentId);
  const registriesLink = getRegistriesLink(environmentType);

  const isLoading =
    registryQuery.isInitialLoading ||
    pingQuery.isInitialLoading ||
    repositoriesQuery.isInitialLoading;

  const breadcrumbs = [
    {
      label: 'Registries',
      link: registriesLink,
      linkParams: environmentId ? { endpointId: environmentId } : {},
    },
    ...getBreadcrumbsForRegistry(registry, isAdminUser),
    'Repositories',
  ];

  return {
    registryId,
    environmentId,
    isAdminUser,
    registry,
    showInvalidConfig,
    repositories,
    isLoading,
    breadcrumbs,
  };
}
